<template>
  <div>
    <div class="container-fluid mt-3">
      <div class="d-flex align-items-center flex-wrap mb-4" style="gap: 24px;">
        <!-- Empresa -->
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t('empresa') || 'Empresa' }}:</span>
          <template v-if="showEmpresaBadge">
            <span class="badge badge-primary px-3 py-2 text-uppercase">
              {{ nombreEmpresaSeleccionada || '—' }}
            </span>
          </template>
          <template v-else>
            <el-select
              id="empresaSelect"
              ref="empresaSelect"
              v-model="empresaSeleccionada"
              :placeholder="$t('seleccione_empresa')"
              style="width: 250px"
              filterable
              @change="onEmpresaChange"
            >
              <el-option
                :label="$t('seleccionar') || 'Seleccionar'"
                :value="null"
              />
              <el-option
                v-for="empresa in empresas"
                :key="empresa.id"
                :label="empresa.nombre"
                :value="empresa.id"
              />
            </el-select>
          </template>
        </div>

        <!-- Tipo de Flujo -->
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t('tipo_flujo') }}:</span>
          <el-select v-model="tipoFlujo" style="width: 220px">
            <el-option v-for="op in opcionesTipoFlujo" :key="op.value" :label="op.text" :value="op.value" />
          </el-select>
        </div>

        <!-- Tipo de Inventario -->
        <div class="d-flex align-items-center ml-3">
          <span class="mr-2">{{ $t('tipo_inventario') }}:</span>
          <el-select v-model="tipoInventario" style="width: 220px">
            <el-option v-for="op in opcionesTipoInventario" :key="op.value" :label="op.text" :value="op.value" />
          </el-select>
        </div>

        <!-- Bodega / Ubicación -->
        <div class="d-flex align-items-center ml-3">
          <span class="mr-2">{{ $t('Warehouse / Location') }}:</span>
          <el-select v-model="idBodegaSeleccionada" filterable clearable :placeholder="$t('seleccione')" style="width: 220px">
            <el-option
              v-for="op in opcionesBodega"
              :key="op.id"
              :label="currentLang === 'en' && op.nombreIngles ? op.nombreIngles : op.nombre"
              :value="op.id"
            />
          </el-select>
        </div>
      </div>
    </div>

    <!-- Wizard de carga de archivos: oculto para perfiles de solo lectura -->
    <div class="row my-4 container-fluid" v-if="puedeEscribir">
      <!-- Card 1: Drag & Drop -->
      <div class="col-4"
  :class="{ 'disabled-card': !empresaSeleccionada || (selectedLog && estadoUI(getEstadoFromLog(selectedLog)) === 'ingresado') }">
        <div class="card border-primary position-relative h-100">
          <span class="badge badge-primary position-absolute" style="top: 10px; right: 10px;">1</span>
          <div class="card-body d-flex flex-column" :class="{ 'drag-active': isDragActive }"
            @dragover.prevent="onDragOver" @dragenter.prevent="onDragEnter" @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <h4 class="card-title">{{ $t('subir_archivo') }}</h4>
            <p class="fw-400">{{ $t('instruccion_archivo') }}</p>
            <div class="d-flex flex-column justify-content-center align-items-center mt-4">
              <div class="text-center">
                <i class="el-icon-document" style="font-size: 2.5rem; color: #007bff;"></i>
              </div>
              <h5 class="card-title">{{ $t('arrastra_archivos') }}</h5>
              <p class="card-text fw-400 text-center mt-5">
                {{ $t('o_selecciona_archivo') }}
              </p>
              <button class="btn btn-primary mb-2" @click="triggerFileInput">
                {{ $t('seleccionar_archivo') }}
              </button>
              <input ref="fileInput" type="file" accept=".xls,.xlsx" class="d-none" @change="onFileChange" />
              <p v-if="mensajeError" class="text-danger mt-3">{{ mensajeError }}</p>
              <h5 v-if="file" class="card-title mt-3">{{ $t('archivo_cargado') }}: {{ file.name }}</h5>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 2: Alineación de campos -->
      <div class="col-4"
        :class="{ 'disabled-card': !empresaSeleccionada || (selectedLog && estadoUI(getEstadoFromLog(selectedLog)) === 'ingresado') }">
        <div class="card position-relative h-100">
          <span class="badge badge-primary position-absolute" style="top: 10px; right: 10px;">2</span>
          <div class="card-body d-flex flex-column">
            <h4 class="card-title">{{ $t('alineacion_campos') }}</h4>
            <div class="flex-grow-1 d-flex flex-column justify-content-center">
              <div v-for="(valor, index) in labels" :key="index" class="mb-3"
                   v-if="tipoInventario !== 1 || !(labels[index] in REPUESTOS_OCULTAR)">
                <div class="row align-items-center">
                  <div class="col-5">
                    <p class="p-login mb-0">
                      {{ $t(displayLabels[labels[index]]) }}
                      <span v-if="isMandatoryField(labels[index])" class="text-danger">*</span>
                    </p>
                  </div>
                  <div class="col-7">
                    <el-select
  class="custom-input"
  :disabled="!loaded"
  v-model="columnas[index]"
  filterable
  clearable
  :placeholder="$t('seleccione')"
>
  <!-- Opciones normales -->
  <el-option
    v-for="item in opciones"
    :key="item.value"
    :label="item.text"
    :value="item.value"
  />

  <!-- "No existe" SOLO en: SKU, Brand Name, Part Number, Part Name, Source Category, Equipment model -->
  <el-option
    v-if="isNoExisteField(labels[index])"
    :key="'nx-'+index"
    :label="$t('no_existe') || 'No existe'"
    :value="NO_EXISTE_VALUE"
  />

  <!-- "No aplica" SOLO en: Time of usage, Year of manufacture, Odometer -->
  <el-option
    v-if="isNoAplicaField(labels[index])"
    :key="'na-'+index"
    :label="$t('no_aplica') || 'No aplica'"
    :value="NO_APLICA_VALUE"
  />
</el-select>

                    <!-- Sub-selector de Condición cuando se elige "No Existe" -->
                    <el-select
                      v-if="labels[index] === 'Condicion' && columnas[index] === NO_EXISTE_VALUE"
                      v-model="condicionForzada"
                      class="custom-input mt-1"
                      :placeholder="$t('seleccione') || 'Seleccione'"
                      style="width:100%"
                    >
                      <el-option
                        v-for="op in opcionesCondicion"
                        :key="op.id"
                        :value="op.nombre"
                        :label="currentLang === 'en' && op.nombreIngles ? op.nombreIngles : op.nombre"
                      />
                    </el-select>

                    <!-- Sub-selector de Unidad de Medida cuando se elige "No Existe" -->
                    <el-select
                      v-if="labels[index] === 'Unidad de Medida' && columnas[index] === NO_EXISTE_VALUE"
                      v-model="unidadMedidaForzada"
                      class="custom-input mt-1"
                      :placeholder="$t('seleccione') || 'Seleccione'"
                      style="width:100%"
                    >
                      <el-option
                        v-for="op in opcionesUnidadMedida"
                        :key="op.id"
                        :value="op.nombre"
                        :label="currentLang === 'en' && op.nombreEn ? op.nombreEn : op.nombre"
                      />
                    </el-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 3: Optimización de inventario -->
      <div class="col-4">
        <div class="card position-relative h-100">
          <span class="badge badge-primary position-absolute" style="top:10px; right:10px">3</span>
          <div class="card-body d-flex flex-column">
  <h4 class="card-title text-center">{{ $t('optimizacion_inventario') }}</h4>

  <div class="flex-grow-1 d-flex flex-column justify-content-center align-items-center">

    <!-- 🔹 Estado I: mostrar mensaje desde el detalle -->
    <!--div v-if="selectedLog && isIngresado" class="w-100">
      <div class="alert alert-info" role="alert">
        <h5 class="mb-2">{{ $t('archivo_en_ingreso') || 'Archivo en ingreso' }}</h5>
        <p class="mb-2">{{ mensajeEstadoI }}</p>
        <div class="text-center">
          <button class="btn btn-outline-primary" @click="verResultadoArchivo(selectedLog.token)">
            {{ $t('ver_detalle') || 'Ver detalle' }}
          </button>
        </div>
      </div>
    </div-->

    <!-- 🔹 Otros estados: UI normal de la card -->
    <template>
      <div class="text-center mt-4">
        <i class="el-icon-upload" style="font-size:2.5rem;color:#007bff"></i>
      </div>

      <p class="mt-3 text-muted text-center" v-if="!file && !selectedLog">
        {{ $t('no_archivo_subido') }}
      </p>

      <div v-if="file && loaded" class="row w-100 mt-4">
        <div class="col-6 text-center">
          <button class="btn btn-primary w-100 mb-4" @click="iniciarCarga">
            {{ $t('iniciar_carga') }}
          </button>
        </div>
        <div class="col-6 text-center">
          <button class="btn btn-secondary w-100" @click="resetFields">
            {{ $t('cancelar') }}
          </button>
        </div>
      </div>

      <!-- Nombre del archivo: linkeable solo si está procesado -->
      <p v-if="selectedLog" class="mb-1 text-center">
        <template v-if="isProcesado">
          <a href="#" @click.prevent="verResultadoArchivo(selectedLog.token)">
            {{ (selectedLog && selectedLog.nombreXLSX) || '-' }} 
          </a>
        </template>
      </p>

      <div v-if="selectedLog && estadoUI(getEstadoFromLog(selectedLog)) === 'ingresado'" class="text-center mb-4">
        <h5 class="card-subtitle">{{ $t('archivo_cargado') }}:</h5>
        <p class="card-text">{{ selectedLog.nombreXLSX }}</p>
        <p class="card-text">
  {{ $t('fecha_carga') }} (GMT 0): {{ formatDate(selectedLog.fechaCreacion || selectedLog.feachaCreacion) }}
</p>
      </div>

      <div v-if="isProcessingUpload || (selectedLog && (isIngresado || isProcesado))" class="w-100 mb-3 text-center">
  <div v-if="isProcessingUpload || isLoadingState" class="d-flex align-items-center justify-content-center py-2 text-muted">
    <i class="el-icon-loading mr-2" style="font-size:1.6rem"></i>
    <span>{{ $t('procesando_archivo') || 'Procesando archivo…' }}</span>
  </div>
  <div v-else class="d-flex align-items-center justify-content-center py-2 text-success">
    <i class="el-icon-circle-check mr-2" style="font-size:1.6rem"></i>
    <span>{{ $t('proceso_completado') || 'Proceso completado' }}</span>
  </div>
</div>

      <div v-if="selectedLog && isProcesado" class="row w-100 justify-content-center">
        <div class="col-12 text-center">
          <button class="btn btn-primary" @click="verResultadoArchivo(selectedLog.token)">
            {{ $t('ver_resultado') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</div>
        </div>
      </div>
    </div>

    <div v-if="esSoloLectura || rol == 'admin' || rol === 'ventas inex' || rol === 'cliente'" class="row mt-4 container-fluid">
      <div class="col-12">
        <div v-if="!empresaSeleccionada" class="alert alert-warning text-center">
          🛈 {{ $t('selecciona_empresa_historial') }}
        </div>
        <div v-else>
          <div v-if="rol === 'ventas inex' || rol === 'cliente'" class="text-right mb-3">
            <button class="btn btn-outline-secondary" @click="recargarLogs">
              🔁 {{ $t('recargar_logs') }}
            </button>
          </div>
          <div v-if="archivosLogs.length === 0" class="alert alert-warning text-center">
            ⚠️ {{ $t('no_archivos_cargados') }}
          </div>
          <h3 class="mb-4">{{ $t('historial_carga_maestro') }}</h3>
          <div class="mb-3 col-3">
            <input type="text" class="form-control" v-model="fileNameFilter"
              :placeholder="$t('filtrar_nombre_archivo')" />
          </div>
          <table class="table table-bordered table-hover">
            <thead class="thead-light">
              <tr>
                <th>{{ $t('nombre') }}</th>
                <th>{{ $t('tamano') }}</th>
                <th>{{ $t('cargado_por') }}</th>
                <th>{{ $t('fecha_carga') }} (GMT 0)</th>
                <th>{{ $t('fecha_cambio_estado') }}</th>
                <th>{{ $t('estado') }}</th>
                <th class="text-center">{{ $t('tipo_flujo') }}</th>
                <th v-if="mostrarColumnaRentabilizar">{{ $t('rentabilizar') || 'Rentabilizar' }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
    v-for="log in filteredArchivosLogs"
    :key="(log.token || log.Token || ('id:' + (log.id || Math.random()))) + ':main'"
  >
                <td>
  <!-- Todos los estados abren el archivo: la pantalla de destino decide si se puede
       editar o se muestra en solo lectura (ver ESTADOS_EDITABLES en MaterialesResultFile). -->
  <template v-if="log.token || log.Token">
    <a href="#" @click.prevent="handleOpenLog(log)">
      {{ log.nombreXLSX }}
    </a>
  </template>

  <!-- Sin token no hay a dónde navegar -->
  <template v-else>
    {{ log.nombreXLSX }}
  </template>

  <br> <div class="text-monospace text-muted" style="font-size:.5rem">{{ log.token || log.Token || '-' }}</div>
</td>
                <td>{{ log.pesoKb }} KB </td>
                <td>{{ log.usuario }}</td>
                <td>{{ formatDate(log.fechaCreacion || log.feachaCreacion) }}</td>
                <td>{{ formatDate(log.fechaActualizacion || log.FechaActualizacion) }}</td>
                <td>
  <span v-if="estadoUI(getEstadoFromLog(log)) === 'ingresado'" class="badge badge-danger">
    {{ estadoDesc(log) || $t('ingresado') }}
  </span>

  <span v-else-if="estadoUI(getEstadoFromLog(log)) === 'procesado'" class="badge badge-success">
    {{ estadoDesc(log) || $t('procesado') }}
  </span>

  <span v-else-if="estadoUI(getEstadoFromLog(log)) === 'solicitado'" class="badge badge-warning">
    {{ estadoDesc(log) || $t('solicitado rentabilización') || 'Solicitado Rentabilización' }}
  </span>

  <span v-else-if="estadoUI(getEstadoFromLog(log)) === 'ecommerce'" class="badge badge-info">
    {{ estadoDesc(log) || $t('ecommerce') || 'E-Commerce' }}
  </span>

  <!-- Badge clickeable que abre el modal de error -->
  <button
  v-else-if="['K','J','L'].includes(normalizeEstado(getEstadoFromLog(log)))"
  type="button"
  class="badge badge-danger badge-clickable"
  @click="mostrarError(log)"
  :title="estadoDesc(log) || errorLabel(getEstadoFromLog(log))"
>
  {{ estadoDesc(log) || errorLabel(getEstadoFromLog(log)) }}
</button>

  <!-- Códigos sin manejo específico en la UI (ej. B): se muestra igual con la descripción del diccionario -->
  <span v-else class="badge badge-secondary">
    {{ estadoDesc(log) || getEstadoFromLog(log) }}
  </span>

<!-- Eliminar solo si está en error (J/K/L) -->
<button
  v-if="puedeEscribir && ['K','J','L'].includes(normalizeEstado(getEstadoFromLog(log)))"
  class="btn btn-sm btn-outline-danger btn-icon-trash"
  @click="confirmEliminar(log)"
  :title="`${$t('eliminar') || 'Eliminar'} — ${errorLabel(getEstadoFromLog(log))}`"
  :aria-label="$t('eliminar') || 'Eliminar'"
>
  <i class="el-icon-delete" aria-hidden="true"></i>
</button>

</td>

<!-- Columna Tipo Flujo -->
<td class="text-center align-middle">
  <span v-if="(log.tipoFlujo || log.TipoFlujo || '').trim() === 'FN'" class="badge badge-primary">{{ $t('tipo_flujo_fn') }}</span>
  <span v-else-if="(log.tipoFlujo || log.TipoFlujo || '').trim() === 'NR'" class="badge badge-warning">{{ $t('tipo_flujo_nr') }}</span>
  <span v-else-if="(log.tipoFlujo || log.TipoFlujo || '').trim() === 'DE'" class="badge badge-info">{{ $t('tipo_flujo_de') }}</span>
  <span v-else class="text-muted">—</span>
</td>

<!-- Columna Rentabilizar: el flujo NR (Directo a Cluster) no pasa por rentabilización -->
<td v-if="mostrarColumnaRentabilizar" class="text-center align-middle">
  <b-button
    v-if="puedeEscribir && !esFlujoDirectoCluster(log) && normalizeEstado(getEstadoFromLog(log)) === 'P'"
    size="sm"
    variant="primary"
    @click="confirmarModalEjecucion(log)"
  >
    {{ $t('ejecutar') || 'Ejecutar' }}
  </b-button>
  <span v-else class="text-muted">—</span>
</td>

<!-- Columna Eliminar Carga -->
<td class="text-center align-middle">
  <button
    v-if="puedeEscribir"
    class="btn btn-sm btn-danger"
    @click="confirmEliminarCarga(log)"
    :title="$t('eliminar_carga')"
  >
    {{ $t('eliminar_carga') }}
  </button>
  <span v-else class="text-muted">—</span>
</td>

              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <b-modal
      id="modal-confirmar-ejecucion-mat"
      :title="$t('confirmar_ejecucion') || 'Confirmar ejecución'"
      @ok="confirmarEjecucion"
      :ok-title="$t('si_ejecutar') || 'Sí, ejecutar'"
      :cancel-title="$t('cancelar') || 'Cancelar'"
      ok-variant="danger"
    >
      <p>{{ $t('seguro_ejecutar') || '¿Está seguro de que desea ejecutar el proceso de rentabilización?' }}</p>
    </b-modal>
  </div>
</template>

<script>
import materialService from '@/api/materialService'
import utilsService from '@/api/utilsService'
import { Select, Option } from 'element-ui'
import empresaService from '@/api/empresaService'
import diccionarioService from '@/api/diccionarioService'
import bodegaUbicacionService from '@/api/bodegaUbicacionService'
import unidadMedidaService from '@/api/unidadMedidaService'
// Si SweetAlert no es global en tu proyecto, descomenta la siguiente línea:
 import Swal from 'sweetalert2'

export default {
  name: 'DragAndDropExcel',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      currentUser: null,
      archivoPendienteEjecucion: null,
      notifiedProcessed: {},       // token -> true (para no repetir modal)
      lastEstadoByToken: {},       // token -> 'ingresado' | 'procesado' | 'solicitado' ...
      lastSyncTick: 0,
      pollTimer: null,
      pollIntervalMs: 20000,
      isPolling: false,
      isProcessingUpload: false,
      data: [],
      opciones: [],
      columnas: [],
      archivosLogs: [],
      labels: [
        'SKU',
        'Brand Name',
        'Part Number',
        'Part Name',
        'Categoria origen',
        'Detalles',
        'Precio USD',
        'Cantidad',
        'Condicion',
        'Modelo',
        'Horometro',
        'AnoFabricacion',
        'Odometro',
        'Unidad de Medida',
        'FechaCompra',
        'FechaUltimoMovimiento',
      ],
      displayLabels: {
        'SKU': 'label_sku',
        'Brand Name': 'label_brand_name',
        'Part Number': 'Part Number',
        'Part Name': 'label_part_name',
        'Categoria origen': 'Source Category',
        'Detalles': 'label_detalles',
        'Precio USD': 'Price (USD)',
        'Cantidad': 'Quantity',
        'Condicion': 'label_condicion',
        'Modelo': 'Equipment model',
        'Horometro': 'Time of usage',
        'AnoFabricacion': 'Year of manufacture',
        'Odometro': 'Odometer',
        'Unidad de Medida': 'Unit of Measure',
        'FechaCompra': 'label_fecha_compra',
        'FechaUltimoMovimiento': 'label_fecha_ultimo_movimiento',
      },
      isDragActive: false,
      mensajeError: '',
      file: null,
      loaded: false,
      archivoLog: null,
      fileNameFilter: '',
      empresas: [],
      // Local a esta vista: no se guarda en el store ni en localStorage. Cada visita a
      // /materiales debe obligar a elegir la empresa de nuevo, no arrastrar la de otra pantalla.
      empresaSeleccionada: null,
      tipoFlujo: 'FN',
      tipoInventario: 3,
      condicionForzada: null,
      opcionesCondicion: [],
      unidadMedidaForzada: null,
      opcionesUnidadMedida: [],
      opcionesBodega: [],
      idBodegaSeleccionada: null,
      NO_APLICA_VALUE: '__NO_APLICA__',
      NO_EXISTE_VALUE: '__NO_EXISTE__',

      // Grupos de campos (usa las etiquetas internas de tu array labels)
      NO_EXISTE_FIELDS: ['SKU','Brand Name','Part Number','Categoria origen','Modelo','Detalles','Precio USD','Condicion','Unidad de Medida','AnoFabricacion','Horometro','Odometro','FechaCompra','FechaUltimoMovimiento'],
      NO_APLICA_FIELDS: [],
      MANDATORY_FIELDS: ['Part Name', 'Cantidad'],
      // Campos ocultos para Repuestos y Componentes y su valor forzado en el payload
      REPUESTOS_OCULTAR: {
        'Horometro': 'no existe',
        'AnoFabricacion': 'no existe',
        'Odometro': 'no existe',
      },
    }
  }, 

  computed: {
    currentLang() {
      return localStorage.getItem('language') || 'es';
    },
    opcionesTipoInventario() {
      return [
        { value: 1, text: this.$t('tipo_inventario_repuestos') },
        { value: 2, text: this.$t('tipo_inventario_equipos') },
        { value: 3, text: this.$t('tipo_inventario_todos') },
      ];
    },
    opcionesTipoFlujo() {
      return [
        { value: 'FN', text: this.$t('tipo_flujo_fn') },
        { value: 'NR', text: this.$t('tipo_flujo_nr') },
        { value: 'DE', text: this.$t('tipo_flujo_de') },
      ];
    },
    showEmpresaBadge() {
  const r = this.rol; // ya lo tienes en computed
  const n = Array.isArray(this.empresas) ? this.empresas.length : 0;
  // badge solo si es cliente o ventas inex y hay exactamente UNA empresa
  return (r === 'cliente' || r === 'ventas inex') && n === 1;
},
    mensajeEstadoI() {
    const log = this.selectedLog || {};
    // Busca en varios campos posibles
    const candidatos = [
      log.mensaje, log.Mensaje, log.message, log.Message,
      log.observacion, log.Observacion,
      log.comentario, log.Comentario,
      log.descripcion, log.Descripcion,
      (log.detalle && (log.detalle.mensaje || log.detalle.Mensaje)) ? (log.detalle.mensaje || log.detalle.Mensaje) : null
    ].filter(function (x) { return x !== undefined && x !== null && String(x).trim() !== ''; });

    if (candidatos.length > 0) return String(candidatos[0]);
    // Fallback amigable
    return this.$t ? (this.$t('mensaje_estado_i_pendiente') || 'El archivo está en ingreso. Revisa el detalle para más información.') 
                   : 'El archivo está en ingreso. Revisa el detalle para más información.';
  },
    estadoFlujoUI() {
      // pinta SOLO por EstadoFlujo del seleccionado
      return this.mapEstadoFlujo(this.getEstadoFromLog(this.selectedLog));
    },
    isProcesado() { return this.estadoFlujoUI === 'procesado'; },
    isIngresado() { return this.estadoFlujoUI === 'ingresado'; },
    isLoadingState() {
      if (this.isIngresado) return true;
      if (this.isProcesado) {
        const token = this.selectedLog && (this.selectedLog.token || this.selectedLog.Token);
        return !token || !this.notifiedProcessed[token];
      }
      return false;
    },

    nombreEmpresaSeleccionada() {
      const lista = Array.isArray(this.empresas) ? this.empresas : [];
      const e = lista.find(x => this.mismoIdEmpresa(x.id, this.empresaSeleccionada));
      return e ? (e.nombre || e.name || e.razonSocial || '') : '';
    },
    rol() {
      return utilsService.getRol();
    },
    // Si ningún archivo listado puede rentabilizarse —caso típico: una empresa que solo
    // trabaja con flujo NR (Directo a Cluster)— la columna entera deja de tener sentido.
    mostrarColumnaRentabilizar() {
      return (this.filteredArchivosLogs || []).some(log => !this.esFlujoDirectoCluster(log));
    },
    filteredArchivosLogs() {
      if (!this.fileNameFilter) return this.archivosLogs;
      const needle = (this.fileNameFilter || '').toLowerCase();
      return this.archivosLogs.filter((log) => {
        const name = log && log.nombreXLSX ? String(log.nombreXLSX).toLowerCase() : '';
        return name.indexOf(needle) !== -1;
      });
    },
    selectedLog() {
    // 1) Prioriza SIEMPRE lo que trae el polling
    if (this.archivoLog) {
      // Si existe en la tabla, devuelve esa referencia (mantiene reactividad por $set)
      const archTok = this.archivoLog.token || this.archivoLog.Token;
const idx = Array.isArray(this.archivosLogs)
  ? this.archivosLogs.findIndex(x => (x.token || x.Token) === archTok)
  : -1;
return idx >= 0 ? this.archivosLogs[idx] : this.archivoLog;
    }

    // 2) Fallback: si no hay archivoLog, usa el más "reciente" de la lista
    if (Array.isArray(this.archivosLogs) && this.archivosLogs.length > 0) {
      const getFecha = (log) => new Date(
        log.fechaActualizacion || log.feachaCreacion || log.fechaCreacion || 0
      ).getTime();
      const ordenados = [...this.archivosLogs].sort((a, b) => getFecha(b) - getFecha(a));
      return ordenados[0] || null;
    }

    return null;
  },
  }, // 👈 cierra computed
  watch: {
  selectedLog(newVal) {
    if (newVal) {
      const token = newVal.token || newVal.Token;
      if (token) {
        const now  = this.estadoUI(this.getEstadoFromLog(newVal));
        const prev = this.lastEstadoByToken[token];
        const transitionedToP = (now === 'procesado') && ((prev && prev !== 'procesado') || (!prev && this.isIngresado));
        this.$set(this.lastEstadoByToken, token, now);
        if (transitionedToP) {
          const nombre = newVal.nombreXLSX || newVal.NombreXLSX || '';
          this.showProcessedDialog(token, nombre);
        }
      }
    }
  }
},
mounted: async function () {
  this.$store.dispatch('setSidebarCollapsed', false);
  const rol = String((utilsService.getUserInfo() || {}).rol || '').toLowerCase();
  if (rol === 'ventas inex') {
    await this.cargarEmpresasVentasInex();
  } else {
    await this.cargarEmpresasInicial();
  }
  try {
    const items = await diccionarioService.getByTipo('CONDICION');
    this.opcionesCondicion = Array.isArray(items)
      ? items.slice().sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'))
      : [];
  } catch (e) {
    this.opcionesCondicion = [];
  }
  try {
    const items = await bodegaUbicacionService.getAll();
    this.opcionesBodega = Array.isArray(items) ? items : [];
  } catch (e) {
    this.opcionesBodega = [];
  }
  try {
    const items = await unidadMedidaService.getAll();
    this.opcionesUnidadMedida = Array.isArray(items)
      ? items.slice().sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'))
      : [];
  } catch (e) {
    this.opcionesUnidadMedida = [];
  }
  // Si la empresa ya estaba guardada (store) y las funciones anteriores no cargaron datos, disparar el evento
  if (this.empresaSeleccionada && !this.archivosLogs.length) {
    await this.onEmpresaChange();
  }
  this.$nextTick(() => {
    const el = document.getElementById('empresaSelect');
    if (el) {
      const input = el.querySelector('input');
      if (input) input.setAttribute('spellcheck', 'false');
    }
  });
},

  activated() {
    // Si el componente es keep-alive y volvemos a él, limpiar el flag si el log ya está procesado
    const log = this.selectedLog;
    if (log) {
      const token = log.token || log.Token;
      if (token && this.notifiedProcessed[token]) {
        this.isProcessingUpload = false;
      }
    }
  },
  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this.stopPolling();
  },

  methods: {
    // Los ids del store vienen de localStorage (Number) y los de la API como int:
    // se comparan normalizados para no depender del tipo.
    mismoIdEmpresa(a, b) {
      if (a == null || b == null) return false;
      return Number(a) === Number(b);
    },

    // El select se pinta con el id guardado en el store ANTES de que llegue la lista de
    // empresas. Mientras no exista la <el-option> correspondiente, element-ui muestra el
    // valor crudo (ej. "21") en vez del nombre y no marca nada en el desplegable.
    // Al terminar la carga se reconcilia: si el id no pertenece a la lista se descarta,
    // y si sí pertenece se fuerza a element-ui a recalcular la etiqueta (su watcher de
    // options omite el recálculo cuando el foco está dentro del propio select).
    sincronizarEmpresaSeleccionada() {
      const id = this.empresaSeleccionada;
      const lista = Array.isArray(this.empresas) ? this.empresas : [];
      // Sin lista (API caída o sin permisos) no se toca la selección guardada.
      if (id == null || lista.length === 0) return;

      if (!lista.some(e => this.mismoIdEmpresa(e.id, id))) {
        console.warn('[materiales] La empresa guardada no está en la lista del usuario:', id);
        this.empresaSeleccionada = null;
        return;
      }

      this.$nextTick(() => {
        const sel = this.$refs.empresaSelect;
        if (sel && typeof sel.setSelected === 'function') sel.setSelected();
      });
    },

    async cargarEmpresasVentasInex() {
  const user = utilsService.getUserInfo() || {};
  let empresas = [];

  // 1) Asociaciones por usuario (API lee el userId del token)
  try {
    empresas = await empresaService.getEmpresasByUser();
  } catch (e) {
    empresas = [];
  }

  // 2) Si viene vacío: intenta "como cliente"
  if ((!empresas || empresas.length === 0) && user && user.clienteId) {
    try {
      empresas = await empresaService.getEmpresasByCliente(user.clienteId);
    } catch (e) {
      empresas = [];
    }
  }

  // 3) Si sigue vacío: usa idEmpresa desde la ruta
  if ((!empresas || empresas.length === 0) &&
      this.$route && this.$route.params && this.$route.params.idEmpresa) {
    const idRuta = parseInt(this.$route.params.idEmpresa, 10);
    if (!isNaN(idRuta) && idRuta > 0) {
      this.empresas = [{ id: idRuta, nombre: user.empresaNombre || user.empresa || 'Empresa' }];
      this.empresaSeleccionada = idRuta;
      if (typeof this.onEmpresaChange === 'function') await this.onEmpresaChange();
      return; // 👈 IMPORTANTE
    }
  }

  // 4) Normaliza y autoselecciona
  const normalizadas = (empresas || []).map(e => ({
    id: (e.id != null ? e.id : e.Id),
    nombre: (e.nombre != null ? e.nombre : e.Nombre)
  })).filter(e => e.id != null);

  this.empresas = normalizadas;
  this.sincronizarEmpresaSeleccionada();

  if (this.empresas.length > 0) {
    return; // no autoselect, el usuario elige
  }

  // 5) Llegas aquí SOLO si realmente no hay empresas
  console.warn('Usuario sin empresas asociadas en la API ni en el perfil.');
  this.$nextTick(() => {
    Swal.fire({
      icon: 'info',
      title: this.$t('sin_empresas') || 'Sin empresas',
      text: this.$t('contacte_soporte_empresas') || 'Tu usuario no tiene empresas asignadas. Contacta a soporte.'
    });
  });
},
    async cargarEmpresasInicial() {
  const user = utilsService.getUserInfo() || {};
  const rol  = (user.rol ? String(user.rol) : '').toLowerCase();
  let empresas = [];

  // 1) Asociaciones por usuario (API resuelve userId del token)
  try {
    empresas = await empresaService.getEmpresasByUser();
    //console.log('Empresas byUser ->', Array.isArray(empresas) ? empresas : []);
  } catch (e) {
    console.warn('byUser fallo:', e);
  }

  // 2) Si VIENE VACÍO y el rol es CLIENTE o VENTAS INEX, intenta "como cliente"
  const puedeActuarComoCliente = (rol === 'cliente' || rol === 'ventas inex');
  if ((!empresas || empresas.length === 0) && puedeActuarComoCliente && user.clienteId) {
    try {
      empresas = await empresaService.getEmpresasByCliente(user.clienteId);
      //console.log('Empresas byCliente (fallback ventas/cliente) ->', empresas);
    } catch (e) {
      console.warn('byCliente fallo:', e);
    }
  }

// 2.5) Solo ADMIN puede caer a /api/empresas (todas)
if ((!empresas || empresas.length === 0) && rol === 'admin') {
  try {
    empresas = await empresaService.getEmpresas();
    console.log('Empresas fallback (admin -> todas):', empresas);
  } catch (e) {
    console.warn('fallback a todas (admin) fallo:', e);
  }
}

  // 3) Normaliza (Id/id, Nombre/nombre)
  const normalizadas = (empresas || []).map(e => ({
    id: (e && (e.id != null ? e.id : e.Id)),
    nombre: (e && (e.nombre != null ? e.nombre : e.Nombre))
  })).filter(e => e && e.id != null);

  this.empresas = normalizadas;
  this.sincronizarEmpresaSeleccionada();

  // 4) Si HAY empresas
  if (this.empresas.length > 0) {
    // Para cliente: autoseleccionar la primera (solo ve sus empresas asignadas)
    if (rol === 'cliente') {
      this.empresaSeleccionada = this.empresas[0].id;
      await this.onEmpresaChange();
    }
    return;
  }

  // 5) Si SIGUE vacío y el rol es CLIENTE o VENTAS INEX, intenta con idEmpresa de la ruta
  if (puedeActuarComoCliente) {
    const idRuta = parseInt(this.$route && this.$route.params && this.$route.params.idEmpresa, 10);
    if (!isNaN(idRuta) && idRuta > 0) {
      this.empresas = [{ id: idRuta, nombre: (user.empresaNombre || user.empresa || 'Empresa') }];
      this.empresaSeleccionada = idRuta;
      await this.onEmpresaChange();
      return;
    }
  }

  // 6) Último fallback: datos del perfil (para cliente) si existieran
  if (puedeActuarComoCliente && user.empresaId && (user.empresaNombre || user.empresa)) {
    this.empresas = [{ id: user.empresaId, nombre: user.empresaNombre || user.empresa }];
    this.empresaSeleccionada = user.empresaId;
    await this.onEmpresaChange();
    return;
  }

  // 7) Definitivamente sin empresas
  console.warn('Usuario sin empresas asociadas en la API ni en el perfil.');
  this.$nextTick(() => {
    Swal.fire({
      icon: 'info',
      title: this.$t('sin_empresas') || 'Sin empresas',
      text: this.$t('contacte_soporte_empresas') || 'Tu usuario no tiene empresas asignadas. Contacta a soporte para asignarlas.'
    });
  });
},
    isNoExisteField(label) {
      return this.NO_EXISTE_FIELDS.indexOf(label) !== -1;
    },
    isNoAplicaField(label) {
      return this.NO_APLICA_FIELDS.indexOf(label) !== -1;
    },
    isMandatoryField(label) {
      return this.MANDATORY_FIELDS.indexOf(label) !== -1;
    },
    errorLabel(estadoRaw) {
  const e = this.normalizeEstado(estadoRaw);
  if (e === 'J') return this.$t('error_categorizacion') || 'Error Categorización';
  if (e === 'L') return this.$t('error_clusterizacion') || 'Error Clusterización';
  // default K u otros que mapees a error:
  return this.$t('error_rentabilizacion') || 'Error Rentabilización';
},
    normalizeEstado(v) {
  return String(v == null ? '' : v).trim().toUpperCase();
},
// "NR" (Directo a Cluster): el archivo salta la rentabilización y se clusteriza
// directamente, así que no se le ofrece la acción de rentabilizar.
esFlujoDirectoCluster(log) {
  return String((log && (log.tipoFlujo || log.TipoFlujo)) || '').trim().toUpperCase() === 'NR';
},
firstDefined() {
  for (let i = 0; i < arguments.length; i++) {
    const v = arguments[i];
    if (v !== null && v !== undefined) return v;
  }
  return null;
},
async mostrarError(archivo) {
  try {
    const title = this.errorLabel(this.getEstadoFromLog(archivo));
    const fallback = 'No hay detalles de error disponibles.';

    // 1) Mensaje local del objeto
    let msg = this.firstDefined(
      archivo && archivo.MensajeError,
      archivo && archivo.mensajeError,
      archivo && archivo.Error,
      archivo && archivo.error,
      archivo && archivo.mensajeErrorCategoria,
      archivo && archivo.MensajeErrorCategoria,
      archivo && archivo.mensajeErrorClusterizacion,
      archivo && archivo.MensajeErrorClusterizacion,
      archivo && archivo.detalle && (archivo.detalle.mensajeError || archivo.detalle.MensajeError),
      archivo && archivo.detalle && (archivo.detalle.mensaje || archivo.detalle.Mensaje),
      null
    );
    if (typeof msg === 'string') msg = msg.trim();

    // 2) Si no vino en el objeto, búscalo en la lista por token/Token
    if (!msg && archivo && (archivo.token || archivo.Token)) {
      const fila = (this.archivosLogs || []).find(a => (a.token || a.Token) === (archivo.token || archivo.Token));
      if (fila) {
        msg = this.firstDefined(fila.MensajeError, fila.mensajeError, fila.Error, fila.error, null);
        if (typeof msg === 'string') msg = msg.trim();
      }
    }

    // 3) Si ya hay mensaje, mostrarlo
    if (msg) {
      await Swal.fire({
        icon: 'error',
        title,
        html: `<pre style="text-align:left;white-space:pre-wrap;">${msg}</pre>`
      });
      return;
    }

    // 4) Fallback al backend
    const resp = await materialService.getFileLog(archivo.token || archivo.Token);
    let serverMsg = this.firstDefined(
      resp && resp.MensajeError,
      resp && resp.mensajeError,
      resp && resp.detalle && (resp.detalle.mensajeError || resp.detalle.MensajeError),
      ''
    );
    if (typeof serverMsg === 'string') serverMsg = serverMsg.trim();

    await Swal.fire({
      icon: 'error',
      title,
      html: `<pre style="text-align:left;white-space:pre-wrap;">${serverMsg || fallback}</pre>`
    });
  } catch (e) {
    console.error('mostrarError()', e);
    await Swal.fire({
      icon: 'error',
      title: this.errorLabel(this.getEstadoFromLog(archivo)),
      text: 'No se pudo obtener el detalle del error.'
    });
  }
},
    mergeDefined(target, patch) {
  const out = { ...(target || {}) };
  if (patch && typeof patch === 'object') {
    Object.keys(patch).forEach(k => {
      const v = patch[k];
      // solo sobreescribe si viene con valor "real"
      if (v !== null && v !== undefined && v !== '') {
        out[k] = v;
      }
    });
  }
  return out;
},
async confirmEliminar(log) {
  if (!this.puedeEscribir) return;
  const token = (log && (log.token || log.Token)) || null;
  if (!token) return;

  const code = this.normalizeEstado(this.getEstadoFromLog(log));
if (!['K','J','L'].includes(code)) {
  Swal.fire({
    icon: 'info',
    title: this.$t('no_permitido') || 'No permitido',
    text: this.$t('solo_eliminar_error') || 'Solo se puede eliminar cuando el archivo está en estado de ERROR (K, J o L).'
  });
  return;
}

  const resp = await Swal.fire({
    title: this.$t('confirmar_eliminacion') || '¿Eliminar este archivo?',
    text: (this.$t('accion_irreversible') || 'Esta acción no se puede deshacer.') +
          ` ${log.nombreXLSX ? `(${log.nombreXLSX})` : ''}`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: this.$t('eliminar') || 'Eliminar',
    cancelButtonText: this.$t('cancelar') || 'Cancelar',
    reverseButtons: true
  });
  if (!resp.isConfirmed) return;

  try {
    await materialService.deleteLogByToken(token);

    // Quitar de la lista local
    this.archivosLogs = (this.archivosLogs || []).filter(x => (x.token || x.Token) !== token);

    if (this.archivoLog && (this.archivoLog.token || this.archivoLog.Token) === token) {
      this.archivoLog = null;
    }

    // Limpiar caches asociados
    this.$delete(this.lastEstadoByToken, token);
    this.$delete(this.notifiedProcessed, token);

    Swal.fire({
      icon: 'success',
      title: this.$t('eliminado') || 'Eliminado',
      text: this.$t('archivo_eliminado_ok') || 'El archivo fue eliminado correctamente.',
      timer: 1800,
      showConfirmButton: false
    });
  } catch (e) {
    console.error('[deleteLogByToken] error:', e);
    const msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data)) || '';
    Swal.fire({
      icon: 'error',
      title: this.$t('error') || 'Error',
      text: (this.$t('no_se_pudo_eliminar') || 'No se pudo eliminar.') + (msg ? ` (${msg})` : '')
    });
  }
},
async confirmEliminarCarga(log) {
  if (!this.puedeEscribir) return;
  const token = (log && (log.token || log.Token)) || null;
  if (!token) return;

  // Verificar si hay fichas publicadas antes de permitir eliminar
  try {
    const apiBase = process.env.VUE_APP_API_URL || '';
    const checkResp = await fetch(
      `${apiBase}/api/cluster/getFichasPublicadasPorToken/${encodeURIComponent(token)}`,
      {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('user-session'),
          'Accept': 'application/json'
        }
      }
    );

    if (checkResp.ok) {
      const data = await checkResp.json();
      if (Array.isArray(data) && data.length > 0) {
        // Solo el nombre del archivo (sin duplicar): pueden ser cientos de fichas
        const nombresArchivos = [...new Set(data.map(f => f.nombreXLSX || '—'))];
        const items = nombresArchivos.map(n => `<li style="margin-bottom:4px"><b>${n}</b></li>`).join('');

        const result = await Swal.fire({
          icon: 'error',
          title: this.$t('no_permitido'),
          html: `<p>${this.$t('archivo_fichas_publicadas')}</p>
                 <ul style="text-align:left;padding-left:1.2rem">${items}</ul>
                 <p style="margin-top:8px"><b>${this.$t('revise_modulo_ecommerce')}</b></p>`,
          confirmButtonText: this.$t('entendido'),
          showCancelButton: true,
          cancelButtonText: this.$t('ir_a_ficha'),
          cancelButtonColor: '#17a2b8'
        });

        if (result.dismiss === Swal.DismissReason.cancel) {
          sessionStorage.setItem('lecturaVentasEstado', JSON.stringify({
            empresaSeleccionada: this.empresaSeleccionada,
            tokenActual: token,
            archivoSeleccionado: log
          }));
          window.open('/lectura-ventas-inventario', '_blank');
        }
        return; // bloquear la eliminación
      }
    }
  } catch (e) {
    console.error('Error al verificar fichas publicadas:', e);
  }

  // Sin fichas publicadas: continuar con la confirmación original
  const resp = await Swal.fire({
    title: this.$t('confirmar_eliminar_carga'),
    text: this.$t('texto_eliminar_carga'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: this.$t('eliminar_carga'),
    cancelButtonText: this.$t('cancelar') || 'Cancelar',
    reverseButtons: true
  });
  if (!resp.isConfirmed) return;

  try {
    await materialService.eliminarCarga(token);

    this.archivosLogs = (this.archivosLogs || []).filter(x => (x.token || x.Token) !== token);
    if (this.archivoLog && (this.archivoLog.token || this.archivoLog.Token) === token) {
      this.archivoLog = null;
    }
    this.$delete(this.lastEstadoByToken, token);
    this.$delete(this.notifiedProcessed, token);

    Swal.fire({ icon: 'success', title: this.$t('eliminado'), timer: 1800, showConfirmButton: false });
  } catch (e) {
    const msg = (e && e.response && e.response.data && (e.response.data.error || e.response.data)) || (e && e.message) || '';
    Swal.fire({ icon: 'error', title: this.$t('error') || 'Error', text: msg || this.$t('no_se_pudo_eliminar') || 'No se pudo eliminar la carga.' });
  }
},
    setInitialEstadoToken(log) {
  if (!log) return;
  const token = log.token || log.Token;
  if (!token) return;
  const est = this.estadoUI(this.getEstadoFromLog(log));
  this.$set(this.lastEstadoByToken, token, est);
  // Si ya está procesado al cargar, marcarlo como notificado para no mostrar spinner
  if (est === 'procesado') {
    this.$set(this.notifiedProcessed, token, true);
    this.isProcessingUpload = false;
  }
},
    async showProcessedDialog(token, nombreXLSX) {
  if (!token) return;

  // avisamos por este token, no repetir
  if (this.notifiedProcessed[token]) return;
  this.$set(this.notifiedProcessed, token, true);

  const title = this.$t ? (this.$t('procesado_ok_titulo') || 'El archivo se ha procesado correctamente') 
                        : 'El archivo se ha procesado correctamente';
  const text  = this.$t ? (this.$t('procesado_ok_texto')  || 'Ya puedes revisar el archivo.') 
                        : 'Ya puedes revisar el archivo.';

  await Swal.fire({
    title,
    text: nombreXLSX ? `${text} (${nombreXLSX})` : text,
    icon: 'success',
    confirmButtonText: this.$t('entendido'),
    allowOutsideClick: true,
  });
  this.isProcessingUpload = false;
},

    handleOpenLog(log) {
  const estado = this.estadoUI(this.getEstadoFromLog(log));
  const token  = log.token || log.Token;

  if (!token) return;

  // "Solicitado" conserva su destino propio: la pantalla de rentabilización.
  if (estado === 'solicitado') {
    this.$router.push({ path: '/rentabilizacion', query: { token, empresa: this.empresaSeleccionada } });
    return;
  }

  // Todo lo demás abre el detalle del archivo. Los estados que no admiten cambios
  // (I ingresado, J/K/L errores, E e-commerce) se muestran allí en solo lectura.
  this.verResultadoArchivo(token);
},
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(this.tickPoll, this.pollIntervalMs);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async tickPoll() {
  if (this.isPolling || !this.empresaSeleccionada) return;
  this.isPolling = true;

  try {
    // 1) token del archivo que estamos mirando (prioriza el que ya está en pantalla)
    const tokenSel =
      (this.archivoLog && (this.archivoLog.token || this.archivoLog.Token)) ||
      (this.selectedLog && (this.selectedLog.token || this.selectedLog.Token)) ||
      null;

    // 2) lee desde backend
    let latest = null;
    if (tokenSel) {
      // 👈 usa el método que YA tienes en materialService.vue
      latest = await materialService.getFileLog(tokenSel);
    } else {
      latest = await materialService.getLastLogFile(this.empresaSeleccionada);
      latest = Array.isArray(latest) ? (latest[0] || null) : latest;
    }
    if (!latest) return;

    // ===== Normalizaciones y lecturas seguras =====
    const latestToken = latest.token || latest.Token || null;

    // Fechas (ms) para detectar actualización aunque el estado no cambie
    const getMs = v => (v ? new Date(v).getTime() : 0);
    const latestMs = getMs(latest.fechaActualizacion || latest.FechaActualizacion);

    const sel = this.selectedLog || null;
    const selToken = sel ? (sel.token || sel.Token) : null;
    const selMs = sel ? getMs(sel.fechaActualizacion || sel.FechaActualizacion) : 0;

    // Estados crudos y mapeados a UI (ingresado/procesado/solicitado)
    const newEstadoRaw = this.getEstadoFromLog(latest);
    const oldEstadoRaw = sel ? this.getEstadoFromLog(sel) : null;
    const newEstadoUI  = this.estadoUI(newEstadoRaw);
    const oldEstadoUI  = this.estadoUI(oldEstadoRaw);

    const sameToken    = !!selToken && !!latestToken && (latestToken === selToken);
    const estadoCambio = newEstadoUI !== oldEstadoUI;
    const fechaSubio   = latestMs > selMs;

    // ===== Condición de refresco =====
    // - cambia token,
    // - cambia estado,
    // - sube la fecha,
    // - o YA está procesado (forzamos finalizar barra)
    const debeRefrescar =
      !sameToken || estadoCambio || fechaSubio || (newEstadoUI === 'procesado');

      if (debeRefrescar) {
  // 1) actualiza el seleccionado sin perder campos ya pintados (como nombreXLSX)
this.archivoLog = this.mergeDefined(this.archivoLog, latest);

const idx = Array.isArray(this.archivosLogs)
  ? this.archivosLogs.findIndex(x => (x.token || x.Token) === latestToken)
  : -1;

if (idx >= 0) {
  // 2) merge protegido sobre la fila existente
  const merged = this.mergeDefined(this.archivosLogs[idx], latest);
  this.$set(this.archivosLogs, idx, merged);
} else {
  // 3) si es nuevo, inserta; (por si viniera sin nombre, al menos no pisas nada existente)
  this.archivosLogs = [ this.mergeDefined({}, latest), ...(this.archivosLogs || []) ];
}

  // --- NUEVO: mostrar modal solo si hubo transición (!P -> P) ---
  // Barra de progreso
// --- Mostrar modal sólo cuando hay transición real a "procesado" ---
const prev = this.lastEstadoByToken[latestToken]; // puede venir undefined (primera vez)
const transitionedToP =
  (newEstadoUI === 'procesado') &&
  (
    (prev && prev !== 'procesado')            // teníamos estado previo y no era P
    || (!prev && oldEstadoUI === 'ingresado') // sin previo, pero veníamos viendo I
  );

// actualiza el último estado visto SIEMPRE
this.$set(this.lastEstadoByToken, latestToken, newEstadoUI);

if (transitionedToP) {
  const token  = latest.token || latest.Token;
  const nombre = latest.nombreXLSX || latest.NombreXLSX || '';
  this.showProcessedDialog(token, nombre);
}
}

    // Re-sync suave de la lista cada 5 ticks
    this.lastSyncTick = (this.lastSyncTick || 0) + 1;
    if (this.lastSyncTick % 5 === 0) {
      try {
        const lista = await materialService.getLogsFiles(this.empresaSeleccionada);
        this.archivosLogs = Array.isArray(lista) ? lista : [];
      } catch {
        /* no-op */
      }
    }
  } catch {
    // no-op fallback
  } finally {
    this.isPolling = false;
  }
},

    // Devuelve el valor crudo de EstadoFlujo del log
    // Devuelve el valor crudo "tipo flujo" del log (I/P/R).
    // Si EstadoFlujo viene null, cae al campo textual `estado`/`Estado`.
    getEstadoFromLog(log) {
      if (!log) return null;

      // 1) Preferir EstadoFlujo si existe
      if (log.estadoFlujo !== undefined && log.estadoFlujo !== null) return log.estadoFlujo;
      if (log.EstadoFlujo !== undefined && log.EstadoFlujo !== null) return log.EstadoFlujo;

      // 2) Fallback: mapear `estado` textual a I/P
      var est = (log.estado !== undefined && log.estado !== null) ? log.estado
        : (log.Estado !== undefined && log.Estado !== null) ? log.Estado
          : null;

      if (est) {
        const e = String(est).trim().toUpperCase();
        if (e === 'COMPLETADO' || e === 'COMPLETO' || e === 'PROCESADO' || e === 'PROCESANDO') return 'P';
        if (e === 'INGRESADO') return 'I';
      }
      return null;
    },

  
    // Normaliza la respuesta del servicio a un objeto que la vista entiende
    updateUltimoLog(raw) {
      if (!raw) { this.ultimoLog = null; return; }
      const a = Array.isArray(raw) ? raw[0] : raw;

      var estadoFlujo = null;
      if (a.estadoFlujo !== undefined && a.estadoFlujo !== null) estadoFlujo = a.estadoFlujo;
      else if (a.EstadoFlujo !== undefined && a.EstadoFlujo !== null) estadoFlujo = a.EstadoFlujo;

      var pesoKb = null;
      if (a.pesoKb !== undefined && a.pesoKb !== null) pesoKb = a.pesoKb;
      else if (a.PesoKb !== undefined && a.PesoKb !== null) pesoKb = a.PesoKb;

      this.ultimoLog = {
        token: a.token || a.Token || null,
        nombreXLSX: a.nombreXLSX || a.NombreXLSX || '-',
        estadoFlujo: estadoFlujo,
        // opcional: dejamos 'estado' por compatibilidad pero NO lo usamos para pintar
        estado: a.estado || a.Estado || null,
        fechaActualizacion: a.fechaActualizacion || a.FechaActualizacion || null,
        pesoKb: pesoKb
      };
    },
    etiquetaEstadoLog(log) {
      return this.estadoUI(this.getEstadoFromLog(log));
    },
    // Descripción del estado (dbo.EstadosProceso) según el idioma activo
    estadoDesc(log) {
      if (!log) return '';
      return this.currentLang === 'en'
        ? (log.estadoFlujoDescEn || log.estadoFlujoDesc || '')
        : (log.estadoFlujoDesc || log.estadoFlujoDescEn || '');
    },
    // Devuelve texto de UI (ingresado / procesado / solicitado)
    estadoUI(estadoRaw) {
  const e = String(estadoRaw || '').trim().toUpperCase();

  // códigos de flujo
  if (e === 'I') return 'ingresado';
  if (e === 'S') return 'solicitado';
  if (e === 'E') return 'ecommerce';
  if (e === 'K' || e === 'J' || e === 'L') return 'error'; 
  if (e === 'P' || e === 'R' || e === 'T' || e === 'C') return 'procesado';

  // compatibilidad con textos
  if (e === 'COMPLETADO' || e === 'COMPLETO' || e === 'PROCESADO' || e === 'PROCESANDO') return 'procesado';
  if (e === 'INGRESADO') return 'ingresado';
  if (e === 'SOLICITADO') return 'solicitado';

  return (estadoRaw || '').toLowerCase();
},

// Mapea I/P/R/T/C/S a etiquetas de UI para la barra superior (si lo usas ahí)
mapEstadoFlujo(v) {
  const e = String(v || '').trim().toUpperCase();
  if (e === 'I') return 'ingresado';
  if (e === 'S') return 'solicitado';                 // 👈 nuevo
  if (e === 'P' || e === 'R' || e === 'T' || e === 'C') return 'procesado';
  return '';
},
    etiquetaEstado(estadoRaw) {
      return this.estadoUI(estadoRaw);
    },

    async recargarLogs() {
  this.archivosLogs = await materialService.getLogsFiles(this.empresaSeleccionada);

  let l = await materialService.getLastLogFile(this.empresaSeleccionada);
  this.archivoLog = Array.isArray(l) ? (l[0] || null) : l;
  if (this.archivoLog) this.setInitialEstadoToken(this.archivoLog); // <<< AÑADIDO
},

    onDragOver(event) { event.preventDefault(); },
    onDragEnter() { this.isDragActive = true; },
    onDragLeave() { this.isDragActive = false; },
    onDrop(event) {
      if (!this.puedeEscribir) return;
      this.isDragActive = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        this.procesarArchivo(file);
        event.dataTransfer.clearData();
      }
    },
    triggerFileInput() {
      if (!this.puedeEscribir) return; this.$refs.fileInput.click(); },
    onFileChange(event) {
      if (!this.puedeEscribir) return;
      const file = event.target.files[0];
      this.file = file;
      this.$refs.fileInput.value = null;
      if (file) this.procesarArchivo();
    },
    isValidFile(file) {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.xls') || fileName.endsWith('.xlsx');
    },
    resetFields() {
      this.file = null;
      this.archivoLog = null;
      this.data = [];
      this.columnas = [];
      this.loaded = false;
    },
    async onEmpresaChange() {
  if (this.empresaSeleccionada) {
    await this.cargarLogsEmpresa(this.empresaSeleccionada);
    if (this.archivoLog) this.setInitialEstadoToken(this.archivoLog);
    this.stopPolling();
    this.startPolling();
  } else {
    this.stopPolling();
    this.archivoLog = null;
    this.archivosLogs = [];
  }
},
    async procesarArchivo() {
      if (!this.empresaSeleccionada) {
        Swal.fire({ icon: 'warning', title: this.$t('debe_seleccionar_empresa'), confirmButtonText: this.$t('aceptar') });
        return;
      }
      if (!this.file) {
        Swal.fire({ icon: 'warning', title: this.$t('archivo_no_seleccionado'), text: this.$t('seleccione_archivo_antes') });
        return;
      }
      Swal.fire({
        title: this.$t('seguro_subir_archivo'),
        text: this.$t('verifique_archivo_correcto'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('subir'),
        cancelButtonText: this.$t('cancelar')
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            Swal.fire({
              html: `<div style="display:flex;align-items:center;justify-content:center;">
                       <img src="/img/others/loading.gif" alt="Loading" style="width:50px;height:50px;margin-right:15px;">
                       <div><h3 style="color:#006393;">${this.$t('guardando')}</h3></div>
                     </div>`,
              showConfirmButton: false,
              allowOutsideClick: false
            });

            const response = await materialService.processFile(this.file);
            if (!response) {
              Swal.fire({ icon: 'error', title: 'Oops...', text: this.$t('error_subir_archivo') });
              return;
            }

            Swal.fire({
              title: this.$t('archivo_cargado_correctamente'),
              text: this.$t('alinea_columnas_instruccion'),
              icon: 'success',
              showConfirmButton: false,
              timer: 2000,
              background: '#f8f9fa'
            });

            this.data = response;

            // MultiOptions (selected/options) robusto
            const selectedMulti = this.data.find(m =>
              (m.tipo && m.tipo === 'selected') || (m.Tipo && m.Tipo === 'selected')
            );
            const opcionesMulti = this.data.find(m =>
              (m.tipo && m.tipo === 'options') || (m.Tipo && m.Tipo === 'options')
            );

            let columnasSeleccionadas = [];
            if (selectedMulti) columnasSeleccionadas = selectedMulti.options || selectedMulti.Options || [];
            // Emparejar por nombre de campo (value/Value), no por posición: el diccionario
            // del backend puede traer entradas que no están en `labels` (ej. BodegaUbicacion),
            // lo que desalinea los índices si se mapea posicionalmente.
            this.columnas = this.labels.map(label => {
              const match = columnasSeleccionadas.find(o => (o.value || o.Value) === label);
              return (match && (match.selected || match.Selected)) || null;
            });
            this.loaded = true;

            let opcionesDropdown = [];
            if (opcionesMulti) opcionesDropdown = opcionesMulti.options || opcionesMulti.Options || [];
            this.opciones = opcionesDropdown;

          } catch (error) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: error.message });
          }
        } else if (result.isDenied) {
          Swal.close();
        }
      });
    },
    async iniciarCarga() {
      if (!this.puedeEscribir) return;
  if (!this.file || this.columnas.length === 0) {
    Swal.fire({ icon: 'warning', title: this.$t('datos_incompletos'), text: this.$t('cargue_archivo_alinee_campos') });
    return;
  }

  for (const label of this.MANDATORY_FIELDS) {
    const idx = this.labels.indexOf(label);
    const valor = idx !== -1 ? this.columnas[idx] : null;
    if (!valor || valor === this.NO_EXISTE_VALUE || valor === this.NO_APLICA_VALUE) {
      Swal.fire({ icon: 'warning', title: this.$t('datos_incompletos'), text: `${this.$t(this.displayLabels[label])}: ${this.$t('campo_obligatorio_mapeo') || 'Debe seleccionar una columna válida.'}` });
      return;
    }
  }

  if (!this.idBodegaSeleccionada) {
    Swal.fire({ icon: 'warning', title: this.$t('datos_incompletos'), text: this.$t('debe_seleccionar_bodega') });
    return;
  }

  const idxCondicion = this.labels.indexOf('Condicion');
  if (idxCondicion !== -1 && this.columnas[idxCondicion] === this.NO_EXISTE_VALUE && !this.condicionForzada) {
    Swal.fire({ icon: 'warning', title: this.$t('datos_incompletos'), text: this.$t('debe_seleccionar_condicion') });
    return;
  }

  const idxUnidadMedida = this.labels.indexOf('Unidad de Medida');
  if (idxUnidadMedida !== -1 && this.columnas[idxUnidadMedida] === this.NO_EXISTE_VALUE && !this.unidadMedidaForzada) {
    Swal.fire({ icon: 'warning', title: this.$t('datos_incompletos'), text: this.$t('debe_seleccionar_unidad_medida') });
    return;
  }

  const labelFlujo = (this.opcionesTipoFlujo.find(o => o.value === this.tipoFlujo) || {}).text || this.tipoFlujo;
  const opBodegaSeleccionada = this.opcionesBodega.find(o => o.id === this.idBodegaSeleccionada) || {};
  const nombreBodega = (this.currentLang === 'en' && opBodegaSeleccionada.nombreIngles) ? opBodegaSeleccionada.nombreIngles : (opBodegaSeleccionada.nombre || '');
  const confirm = await Swal.fire({
    title: this.$t('seguro_iniciar_carga'),
    html: `<b>${this.$t('archivo')}:</b> ${this.file.name}<br><b>${this.$t('empresa')}:</b> ${this.nombreEmpresaSeleccionada}<br><b>${this.$t('tipo_flujo')}:</b> ${labelFlujo}<br><b>${this.$t('Warehouse / Location')}:</b> ${nombreBodega}`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: this.$t('iniciar'),
    cancelButtonText: this.$t('cancelar')
  });
  if (!confirm.isConfirmed) return;

  const columnasPayload = this.labels.map((label, index) => {
    let columna;
    let subValor = null;

    if (this.tipoInventario === 1 && label in this.REPUESTOS_OCULTAR) {
      columna = this.REPUESTOS_OCULTAR[label];
    } else if (this.columnas[index] === this.NO_APLICA_VALUE) {
      columna = 'no existe';
    } else if (this.columnas[index] === this.NO_EXISTE_VALUE) {
      columna = 'no existe';
      if (label === 'Condicion' && this.condicionForzada) {
        subValor = this.condicionForzada;
      } else if (label === 'Unidad de Medida' && this.unidadMedidaForzada) {
        subValor = this.unidadMedidaForzada;
      }
    } else if (!this.columnas[index]) {
      // Campo no mandatorio dejado en "Seleccione": se envía igual que "No existe"
      columna = 'no existe';
    } else {
      columna = this.columnas[index];
    }

    return { Campo: label, Columna: columna, SubValor: subValor };
  });

  this.isProcessingUpload = true;
  try {
    Swal.fire({
      html: `<div style="display:flex;align-items:center;justify-content:center;">
               <img src="/img/others/loading.gif" alt="Loading" style="width:50px;height:50px;margin-right:15px;">
               <div><h3 style="color:#006393;">${this.$t('procesando_archivo')}</h3></div>
             </div>`,
      showConfirmButton: false,
      allowOutsideClick: false,
    });

    await materialService.uploadFileMateriales(this.file, JSON.stringify(columnasPayload), this.empresaSeleccionada, this.tipoFlujo, this.tipoInventario, this.idBodegaSeleccionada);

    await Swal.fire({
      title: this.$t('archivo_cargado_correctamente'),
      text: this.$t('inicio_procesamiento'),
      icon: 'success',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    // Continúa el flujo normalmente
    this.file = null;
    this.opciones = [];

    let l = await materialService.getLastLogFile(this.empresaSeleccionada);
    this.archivoLog = Array.isArray(l) ? (l[0] || null) : l;
    if (this.archivoLog) this.setInitialEstadoToken(this.archivoLog);

    this.archivosLogs = await materialService.getLogsFiles(this.empresaSeleccionada);

    // Guardar defaults de condicion y bodega si se seleccionó NO EXISTE.
    // Usa el log más reciente de archivosLogs (ordenado DESC) como fallback si archivoLog no tiene token.
    const logReciente = this.archivoLog || (this.archivosLogs && this.archivosLogs[0]) || null;
    const token = logReciente && (logReciente.token || logReciente.Token);
    if (token) {
      const defaults = {};
      if (this.condicionForzada) {
        const opCond = (this.opcionesCondicion || []).find(x => x.nombre === this.condicionForzada);
        if (opCond && opCond.id) defaults.idCondicionDefecto = opCond.id;
      }
      if (this.idBodegaSeleccionada) {
        defaults.idBodegaDefecto = this.idBodegaSeleccionada;
      }
      if (Object.keys(defaults).length > 0) {
        try {
          await materialService.updateDefaults(token, defaults);
        } catch (e) {
          console.error('Error guardando defaults de condicion/bodega:', e);
        }
      }
    }

  } catch (error) {
    this.isProcessingUpload = false;
    Swal.fire({ icon: 'error', title: this.$t('error_carga'), text: this.$t('error_procesar_archivo') });
  }
},
    verResultado() {
      if (this.archivoLog) {
        this.$router.push(`/materialesResultFile/${this.archivoLog.token}`);
      }
    },
    verResultadoArchivo(token) {
      this.$router.push({ path: `/materialesResultFile/${token}`, query: { empresa: this.empresaSeleccionada } });
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const dd   = String(d.getDate()).padStart(2, '0');
      const mm   = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh   = String(d.getHours()).padStart(2, '0');
      const min  = String(d.getMinutes()).padStart(2, '0');
      return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
    },
    async cargarLogsEmpresa(idEmpresa) {
      // Pide por separado para no perder el primero si el segundo falla
      try {
        let l = await materialService.getLastLogFile(idEmpresa);
        this.archivoLog = Array.isArray(l) ? (l[0] || null) : l; // 👈 normaliza
        // ⬇️ LOG del último archivo
        if (this.archivoLog) {
          this.setInitialEstadoToken(this.archivoLog);
          console.log('[INIT] archivoLog:',
            (this.archivoLog.estadoFlujo !== undefined ? this.archivoLog.estadoFlujo : this.archivoLog.EstadoFlujo),
            (this.archivoLog.estado !== undefined ? this.archivoLog.estado : this.archivoLog.Estado),
            this.archivoLog.token
          );
        } else {
          console.log('[INIT] archivoLog: null');
        }
      } catch (error) {
        this.archivoLog = null;
        this.handleError && this.handleError(error, 'No se pudo obtener el último log');
      }

      try {
  const lista = await materialService.getLogsFiles(idEmpresa);
  const normalizada = (Array.isArray(lista) ? lista : []).map(a => {
    // Consolidar MensajeError sin pisarlo
    let msg = this.firstDefined(a.MensajeError, a.mensajeError, a.Error, a.error, null);
    if (typeof msg === 'string') msg = msg.trim();

    return {
      ...a,
      mensajeError: msg || a.mensajeError || a.MensajeError || '' // mantenemos ambos por compatibilidad
    };
  });

  this.archivosLogs = normalizada;
} catch (error) {
  this.archivosLogs = [];
  this.handleError && this.handleError(error, 'No se pudo obtener el historial de logs');
  if (this.archivoLog) this.setInitialEstadoToken(this.archivoLog);
}
    },

    confirmarModalEjecucion(log) {
      if (!this.puedeEscribir || this.esFlujoDirectoCluster(log)) return;
      this.archivoPendienteEjecucion = log;
      this.$bvModal.show('modal-confirmar-ejecucion-mat');
    },

    confirmarEjecucion() {
      if (this.archivoPendienteEjecucion) {
        this.ejecutarArchivo(this.archivoPendienteEjecucion);
        this.archivoPendienteEjecucion = null;
      }
    },

    async ejecutarArchivo(log) {
      const token = log && (log.token || log.Token);
      if (!token) return;

      try {
        const response = await materialService.updateEstadoFlujo(token, 'S');

        if (response && (response.success === true || response.Success === true)) {
          const idx = this.archivosLogs.findIndex(a => (a.token || a.Token) === token);
          if (idx !== -1) {
            this.$set(this.archivosLogs, idx, {
              ...this.archivosLogs[idx],
              estadoFlujo: 'S',
            });
          }
        } else {
          Swal.fire('Error', 'No se pudo iniciar el proceso de rentabilización.', 'error');
        }
      } catch (error) {
        console.error('Error al ejecutar archivo:', error);
        Swal.fire('Error', 'Ocurrió un error al ejecutar el proceso.', 'error');
      }
    },

  }
}
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
  /* Definimos una altura mínima para que todas las cards sean del mismo tamaño */
  min-height: 450px;
}

.drag-active {
  border: 2px dashed #007bff;
  background-color: #f8f9fa;
}

p.p-login {
  margin-bottom: 0 !important;
  font-size: 0.9rem;
  font-weight: 500;
}

p.fw-400 {
  font-size: 0.8rem;
  margin-bottom: 1rem;
}

i.el-icon-document {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #3f8efb;
}

.btn {
  font-size: 0.7rem !important;
}

.btn:not(:last-child) {
  margin-right: inherit !important
}

.badge {
  border-radius: 7rem !important;
}

.disabled-card {
  pointer-events: none;
  opacity: 0.5;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
}

.table th {
  background-color: #f8f9fa;
  text-align: center;
}

.table td,
.table th {
  padding: 0.75rem;
  vertical-align: middle;
  text-align: center;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.progress {
  height: 20px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #e9ecef;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  transition: width 0.4s ease;
}

/* Fallback por si tu build no trae progress-bar-striped/animated */
.progress-bar.progress-bar-striped {
  background-image: linear-gradient(
    45deg,
    rgba(255,255,255,.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255,255,255,.15) 50%,
    rgba(255,255,255,.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 1rem 1rem;
}

@keyframes progress-bar-stripes {
  from { background-position: 1rem 0; }
  to   { background-position: 0 0; }
}

.progress-bar.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}

.bg-info    { background-color: #17a2b8 !important; }
.bg-success { background-color: #28a745 !important; }

button.btn.btn-sm.btn-outline-danger {
    padding: 1px 5px;
    margin-left: 5px;
}
button.badge.badge-danger.badge-clickable {
    border: solid;
}
.btn-icon-trash {
  width: 26px;
  height: 26px;
  padding: 0;                   
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon-trash i {
  font-size: 14px;              
  line-height: 1;
}
.badge-ecommerce {
  background-color: #007bff;
  color: #fff;
}
</style>